import React, { useMemo } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import { shareTiktok } from 'src/app/shared/share/tiktok';

import type { ShareActionComponentPropsWithOptions } from '../types';

import { useEducationModal } from './useEducationModal';

export const TiktokShare: React.FC<ShareActionComponentPropsWithOptions> = ({
	children,
	options,
	onAfterAction,
	onBeforeAction,
}) => {
	/**
	 * useEducationModal handles context detection internally:
	 * - returns [sharedOpenFn, null] when ShareEducationModalProvider is in the tree
	 * - returns [localOpenFn, <Modal>] otherwise (standalone fallback)
	 */
	const [openEducationModal, educationModal] = useEducationModal();
	const track = useTracking();
	const utilityContext = useUtilityContext();

	const onClick = useMemo(() => {
		return () => {
			void (async () => {
				await onBeforeAction?.();

				const {
					url,
					content: { image },
					trackingData,
					trackingEventName,
				} = options;

				const success = await shareTiktok(
					{ url, image },
					{
						trackingData,
						trackingEvents: {
							success: trackingEventName,
							intent: 'tiktok_share_modal_view',
							continue: 'tiktok_share_modal_continue',
							cancel: 'tiktok_share_modal_close',
							failure: `${trackingEventName}_failure`,
						},
						openTiktokEducationModal: ({ onContinueAction }) => openEducationModal({ onContinueAction }),
					},
					track,
					utilityContext,
				);

				await onAfterAction?.(success);
			})();
		};
	}, [onBeforeAction, onAfterAction, options, openEducationModal, track, utilityContext]);

	return (
		<>
			{React.cloneElement(children, {
				...children.props,
				onClick,
			})}
			{educationModal}
		</>
	);
};
